import { useCallback, useEffect, useMemo, useState } from "react";
import { Hotel, HotelDetails } from "../../../assets/Services/wsSearch";
import {
  Map,
  AdvancedMarker,
  APIProvider,
  MapControl,
  useMap,
  CollisionBehavior,
  MapProps,
} from "@vis.gl/react-google-maps";
import { useSearch } from "../../hooks/useSearch";
import { t } from "i18next";
import { useMediaQuery } from "react-responsive";
import { isEmpty } from "lodash";

import { cn, SMALL_SCREEN } from "../../../assets/Utils/generic";
import IconSpinner from "../../../assets/icons/IconSpinner";
import {
  ANALYTICS_SOURCE,
  ANALYTICS_SEARCH_TYPE,
} from "../../../config/analytics";
import { currencySymbols } from "../../../assets/Utils/prices";
import PlusModal from "./PlusModal";
import { openModal } from "../../../assets/Utils/modal";
import IconSearchArea from "../../../assets/icons/IconSearchArea";
import IconNotAllowed from "../../../assets/icons/IconNotAllowed";
import { getDisplayPrice } from "./utils";

const FitBounds = ({ mapPositionProps }: { mapPositionProps: MapProps }) => {
  const { searchParameters } = useSearch();

  const map = useMap();

  useEffect(() => {
    if (map) {
      // if (isEqual(prevMapPositionProps, mapPositionProps)) {
      //   return;
      // }

      const bounds = {
        ...(typeof mapPositionProps.defaultBounds?.north === "number"
          ? {
              north: mapPositionProps.defaultBounds?.north,
            }
          : {}),
        ...(typeof mapPositionProps.defaultBounds?.east === "number"
          ? {
              east: mapPositionProps.defaultBounds?.east,
            }
          : {}),
        ...(typeof mapPositionProps.defaultBounds?.south === "number"
          ? {
              south: mapPositionProps.defaultBounds?.south,
            }
          : {}),
        ...(typeof mapPositionProps.defaultBounds?.west === "number"
          ? {
              west: mapPositionProps.defaultBounds?.west,
            }
          : {}),
      };

      if (!isEmpty(bounds)) {
        if (searchParameters.searchType !== "tag") {
          return;
        }

        map.fitBounds(
          bounds as {
            north: number;
            east: number;
            south: number;
            west: number;
          },
        );
      }
    }
  }, [mapPositionProps]);

  return <></>;
};

interface HotelsMapProps {
  hotels: Hotel[] | HotelDetails[];
  selected: string;
  isSmallScreen?: boolean;
  onClick(hotelId: string): any;
}

export default function HotelsMap({
  hotels,
  selected,
  onClick,
  isSmallScreen = false,
}: HotelsMapProps) {
  const { isLoading, searchParameters, viewport, goToSearchUrl } = useSearch();
  const [hasMapMoved, setHasMapMoved] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  const [key, setKey] = useState(0);

  // Refresh the map when near hotels arrive
  useEffect(() => {
    setHasMapMoved(false);
    setHasUserInteracted(false);
    setKey((prev) => prev + 1);
  }, [isLoading]);

  const mapPositionProps: Partial<MapProps> = useMemo(() => {
    if (viewport) {
      return {
        defaultBounds: {
          north: viewport.northeast.lat,
          east: viewport.northeast.lng,
          south: viewport.southwest.lat,
          west: viewport.southwest.lng,
        },
      };
    }

    if (hotels.length > 0) {
      const v = {
        defaultBounds: {
          north: Math.max(
            ...hotels.map((h) => h.mongoDbLocation.coordinates[1]),
          ),
          south: Math.min(
            ...hotels.map((h) => h.mongoDbLocation.coordinates[1]),
          ),
          east: Math.max(
            ...hotels.map((h) => h.mongoDbLocation.coordinates[0]),
          ),
          west: Math.min(
            ...hotels.map((h) => h.mongoDbLocation.coordinates[0]),
          ),
        },
      };
      return v;
    }

    return {
      defaultBounds: {
        north: 47.092,
        south: 35.49,
        east: 18.5204,
        west: 6.614,
      },
    };
  }, [viewport, hotels]);

  const showMap = useMemo(
    () => !isLoading || !!viewport,
    [isLoading, viewport],
  );

  const renderedPins = useMemo(
    () =>
      hotels.map((hotel, i) => {
        return (
          <CustomPin
            onClick={() => onClick(hotel._id)}
            isSmallScreen={isSmallScreen}
            key={`hotel-pin-${hotel._id}`}
            lat={hotel.mongoDbLocation.coordinates[1]}
            lng={hotel.mongoDbLocation.coordinates[0]}
            text={
              hotel.availability &&
              hotel.availability.availabilityInfo.available
                ? getDisplayPrice({
                    availability: hotel.availability,
                    deltaBooking: hotel.deltaPriceBookingCom,
                    deltaHotels: hotel.deltaPriceHotelsCom,
                    lang: searchParameters.language,
                  })
                : null
            }
            selected={hotel._id === selected}
            // Lower index goes on top, hotels with no price are at the end
            index={i}
            isStructurePlus={!!hotel.isActive}
            isCircle={false} //!priceInfo}
          />
        );
      }),
    [hotels, selected],
  );

  const [currentBounds, setCurrentBounds] =
    useState<google.maps.LatLngBounds>();

  const coordinatesSearch = useCallback(() => {
    if (!currentBounds) return;
    const coordinates = currentBounds.toJSON();

    const boxCoordinates = {
      northeast: {
        lat: coordinates.north,
        lng: coordinates.east,
      },
      southwest: {
        lat: coordinates.south,
        lng: coordinates.west,
      },
    };

    setHasMapMoved(false);
    goToSearchUrl(
      {
        searchType: "box",
        boxCoordinates: boxCoordinates,
        placeId: undefined,
        collectionId: undefined,
        destinationLabel: undefined,
        tag: undefined,
        searchLocationType: undefined,
      },
      {
        triggerTagManagerEvent: false,
        source: ANALYTICS_SOURCE.MAP,
        searchType: ANALYTICS_SEARCH_TYPE.MAP,
      },
    );
  }, [currentBounds, searchParameters]);

  const isMobile = useMediaQuery({ query: SMALL_SCREEN });

  const openPlusModal = () => {
    openModal({
      content: <PlusModal />,
      className:
        "h-fit self-end rounded-t-[1.25rem] lg:h-auto lg:self-center lg:rounded-3xl max-w-[400px]",
      native: isSmallScreen,
    });
  };

  // This is a workaround to make the map rerender when having a new search
  return !showMap && searchParameters.searchType === "city" ? null : (
    <APIProvider apiKey={String(process.env.REACT_APP_GOOGLE_API_KEY)}>
      <Map
        key={key}
        // this is a workaround to avoid the map to be rerendered when navigating
        className={`w-100 h-100 z-10 ${showMap ? "" : "d-none"}`}
        onDragend={(e) => {
          setHasUserInteracted(true);
          setHasMapMoved(true);
          setCurrentBounds(e.map.getBounds());
        }}
        onZoomChanged={(e) => {
          if (!hasUserInteracted) {
            setHasUserInteracted(true);
            return;
          }
          setHasMapMoved(true);
          setCurrentBounds(e.map.getBounds());
        }}
        mapId="RESULTS_MAP"
        fullscreenControl={false}
        mapTypeControl={false}
        streetViewControl={false}
        gestureHandling={"greedy"}
        minZoom={5}
        {...mapPositionProps}
      >
        <>
          <FitBounds mapPositionProps={mapPositionProps} />
          {renderedPins}
          <MapControl position={2}>
            <div className="min-w-36 flex flex-col items-center">
              {isLoading && (
                <div
                  className="bg-[#F8F8F8] flex items-center justify-center px-4 py-3 rounded-2xl gap-2 w-fit mx-auto mt-20 lg:mt-8 ring-2 ring-white ring-inset border-0 shadow-[0px_0px_20px_5px_rgba(0,_0,_0,_0.25)]"
                  onClick={coordinatesSearch}
                >
                  <IconSpinner className="size-4 text-black animate-spin" />
                  <p className="text-sm text-black font-sans">
                    {t("ds.map_loading")}
                  </p>
                </div>
              )}
              {hasMapMoved && hasUserInteracted && !isLoading && (
                <button
                  type="button"
                  className="bg-[#F8F8F8] flex items-center justify-center px-4 py-3 rounded-2xl gap-2 w-fit mx-auto mt-20 lg:mt-8 ring-2 ring-white ring-inset border-0 shadow-[0px_0px_20px_5px_rgba(0,_0,_0,_0.25)]"
                  onClick={coordinatesSearch}
                >
                  <IconSearchArea className="size-[1.125rem] text-black" />
                  <p className="text-sm text-black font-sans">
                    {t("ds.map_reload")}
                  </p>
                </button>
              )}
            </div>
          </MapControl>
        </>
      </Map>
    </APIProvider>
  );
}

interface PinProps {
  lat: number;
  lng: number;
  text: string | null;
  index: number;
  selected: boolean;
  isSmallScreen: boolean;
  onClick(): any;
  isStructurePlus: boolean;
  isCircle: boolean;
}

function CustomPin({
  lat,
  lng,
  text,
  selected,
  onClick,
  index,
  isSmallScreen,
  isStructurePlus,
  isCircle,
}: PinProps) {
  const [isHovered, setIsHovered] = useState(false);
  const map = useMap();
  const zIndex = selected || isHovered ? 1 : -1 * index;

  return (
    <AdvancedMarker
      position={{ lat, lng }}
      onClick={() => {
        if (!isSmallScreen) {
          map?.setCenter({ lat, lng });
        }
        onClick();
      }}
      zIndex={zIndex}
      collisionBehavior={CollisionBehavior.REQUIRED}
    >
      <div
        className={`custom-pin ${selected ? "selected" : ""}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div
          className={cn(
            "rounded-xl border-none ring-1 ring-inset ring-[#F3F3F3] shadow-[0px_0px_20px_5px_rgba(0,_0,_0,_0.25)] bg-white w-fit aspect-square h-9 min-w-14 flex items-center justify-center text-sm font-semibold hover:scale-110 transition-transform duration-300 font-sans",
            isStructurePlus && "bg-[#CFFFCA] ring-[#85DB7F]",
            selected && "bg-[#171717] ring-[#171717] text-white",
            isCircle && "size-5 min-w-0 rounded-full shrink-0",
            text !== null && text.length === 4 && "min-w-14",
            text !== null && text.length === 5 && "min-w-16",
            text !== null && text.length > 5 && "min-w-[4.5rem]",
          )}
        >
          {text !== null ? text : <IconNotAllowed className="size-4" />}
        </div>
      </div>
    </AdvancedMarker>
  );
}
